import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Carte } from '../../../../utils/types/Carte';
import { format } from 'date-fns';
import { useAuth } from '../../../../utils/context/AuthContext';
import apiClient from '../../../../services/api/apiClient';
import axios from 'axios';
import IAlertType from '../../../../utils/types/props/IAlertType';
import AlertComponent from '../../../utility/AlertComponent';
import EditCarteModal from '../../wizard/carte/EditCarteModal';
import { JournalCarte } from '../../../../utils/types/JournalCarte';
import JournalCarteGrid from '../../../grid/JournalCarteGrid';
import CarteGrid from '../../../grid/CarteGrid';
import { formatAxiosError } from '../../../../utils/formatAxiosError';
import { TRechargeCarteMaster } from '../../../../utils/types/tables/TRechargeCarteMaster';
import RechargeCarteMasterGrid from '../../../grid/RechargeCarteMasterGrid';
import { IconEye, IconEyeOff, IconLockOpen } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../others/QueryErrorAlerts';
import { GridLoader } from 'react-spinners';

interface CarteDetailsProps {
    pCarte: Carte;
    codeClient: string;
    onUpdate?: (updatedCarte: Carte) => void;
    onClose?: () => void;
    showModal?: boolean;
}

const CarteDetailsContent: React.FC<CarteDetailsProps> = ({ pCarte, codeClient, onUpdate, showModal }) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    // ---

    const cardStyle = {
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        transition: 'box-shadow 0.3s ease',
    };

    // ---

    const [showPin, setShowPin] = useState(false);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };

    // ---

    const {
        data: carte = null,
        isLoading: isLoadingCarte,
        refetch: refetchCarte,
        isError: isCarteError,
        error: carteError,
    } = useQuery<Carte | null>({
        queryKey: [
            'carte',
            {
                customerCardCode: codeClient,
                numeroCarte: pCarte.numero,
            },
        ],
        queryFn: async () => {
            if (!pCarte.id) {
                return null;
            }

            const res = await apiClient.get('/api/cartes/getCarte', {
                params: {
                    idCarte: pCarte.id,
                },
            });

            return res.data ?? null;
        },
        meta: { title: 'Chargement de la carte' },
        enabled: !!pCarte.id && showModal === true,
        staleTime: 0,
    });

    const {
        data: recharges = [],
        refetch: refetchRecharges,
        isError: isRechargesError,
        error: rechargesError,
    } = useQuery<TRechargeCarteMaster[]>({
        queryKey: [
            'rechargesCartes',
            {
                idCarte: carte?.id,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/cartes/get-recharges-master', {
                params: {
                    idCarte: carte?.id,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des recharges' },
        enabled: !!carte?.id,
        staleTime: 0,
    });

    const {
        data: journalItems = [],
        refetch: refetchJournalItems,
        isError: isJournalItemsError,
        error: journalItemsError,
    } = useQuery<JournalCarte[]>({
        queryKey: [
            'journalCarte',
            {
                idCarte: carte?.id,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/cartes/getJournalCarte', {
                params: {
                    idCarte: carte?.id,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement du journal de la carte' },
        enabled: !!carte?.id,
        staleTime: 0,
    });

    const {
        data: logs = [],
        refetch: refetchLogs,
        isError: isLogsError,
        error: logsError,
    } = useQuery<Carte[]>({
        queryKey: [
            'logsCarte',
            {
                idCarte: carte?.id,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/cartes/getAuditLogs', {
                params: {
                    idCarte: carte?.id,
                },
            });
            return res.data ?? [];
        },
        meta: { title: "Chargement du journal d'audit de la carte" },
        enabled: !!carte?.id,
    });

    // ---

    const { mutate: unlockCard, isPending: isUnlocking } = useMutation({
        mutationFn: () => apiClient.put(`/api/cartes/unlock/${carte!.id}`),
        onSuccess: (response) => {
            if (response.data.success === true) {
                Swal.fire({
                    text: 'La carte a été déverrouillée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                });

                queryClient.invalidateQueries({
                    queryKey: ['carte', { customerCardCode: codeClient, numeroCarte: pCarte.numero }],
                });

                handleLoadCarte();
            }
        },
        onError: (err: any) => {
            if (!axios.isCancel(err)) {
                setSystemAlerts((prev) => [
                    ...prev,
                    {
                        type: 'danger',
                        message: 'Erreur lors du déverrouillage de la carte. ' + formatAxiosError(err)?.message,
                    },
                ]);
            }
        },
    });

    // ---

    const handleLoadCarte = useCallback(() => {
        if (!carte?.id) return;
        refetchCarte();
    }, [refetchCarte, carte]);

    const handleLoadRecharges = useCallback(() => {
        if (!carte?.id) return;
        refetchRecharges();
    }, [refetchRecharges, carte]);

    const handleUnlockCard = async () => {
        const result = await Swal.fire({
            title: 'Déverrouiller la carte ?',
            text: 'La carte sera réactivée et pourra à nouveau être utilisée.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Oui, déverrouiller',
            cancelButtonText: 'Annuler',
        });

        if (result.isConfirmed) {
            unlockCard();
        }
    };

    const handleCarteUpdated = (updatedCarte: Carte) => {
        onUpdate?.(updatedCarte);

        if (updatedCarte != null) {
            queryClient.invalidateQueries({
                queryKey: ['carte', { customerCardCode: codeClient, numeroCarte: pCarte.numero }],
            });

            handleLoadCarte();
        }

        refetchLogs();
    };

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isCarteError, error: carteError, title: 'Erreur lors du chargement de la carte', refetch: handleLoadCarte },
            { isError: isRechargesError, error: rechargesError, title: 'Erreur lors du chargement des recharges', refetch: handleLoadRecharges },
            { isError: isJournalItemsError, error: journalItemsError, title: 'Erreur lors du chargement du journal de la carte', refetch: refetchJournalItems },
            { isError: isLogsError, error: logsError, title: "Erreur lors du chargement du journal d'audit de la carte", refetch: refetchLogs },
        ],
        [isCarteError, carteError, handleLoadCarte, isRechargesError, rechargesError, handleLoadRecharges, isJournalItemsError, journalItemsError, refetchJournalItems, isLogsError, logsError, refetchLogs],
    );

    // ---

    if (isLoadingCarte) {
        return (
            <div className="flex flex-col items-center justify-center">
                <GridLoader size={10} cssOverride={{ display: 'block', margin: '0 auto' }} />
                <p className="mt-2 text-sm text-center">En cours de chargement ...</p>
            </div>
        );
    }

    if (!carte) {
        return (
            <div className="alert alert-important alert-danger alert-dismissible" role="alert">
                <div className="alert-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                </div>
                <div>
                    <span className="fw-bold">Carte non trouvée.</span>
                    <span className="ms-1 text-muted">— Nous n'avons pas pû charger la carte.</span>
                </div>

                <button type="button" className="alert-action btn btn-sm" onClick={handleLoadCarte} disabled={!pCarte?.id}>
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            {isUnlocking && (
                <div className="flex flex-col items-center justify-center gap-2">
                    <GridLoader size={10} cssOverride={{ display: 'block', margin: '0 auto' }} />
                    <p className="text-center text-sm">En cours de déverrouillage ...</p>
                </div>
            )}

            <div className="card">
                <div className="card-header">
                    <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                        <li className="nav-item">
                            <a href="#tabs-infos" className="nav-link active" data-bs-toggle="tab">
                                Informations
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="#tabs-recharges" className="nav-link" data-bs-toggle="tab">
                                Recharges
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="#tabs-journal" className="nav-link" data-bs-toggle="tab">
                                Relevé
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="#tabs-log" className="nav-link" data-bs-toggle="tab">
                                Audit
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="card-body">
                    <div className="tab-content">
                        <div className="tab-pane active show" id="tabs-infos">
                            <>
                                <div className="row">
                                    <div className="col-xl-4 col-lg-6">
                                        <div className="card" style={cardStyle}>
                                            <div className="card-body">
                                                <div className="col-md-12">
                                                    <label htmlFor="numero">Numéro</label>
                                                    <input type="text" id="numero" className="form-control" value={carte.numero ?? ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label htmlFor="type">Type Carte</label>
                                                    <input type="text" id="type" className="form-control" value={carte.type ?? ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label htmlFor="porteur">Porteur</label>
                                                    <input type="text" id="porteur" className="form-control" value={carte.description ?? ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label htmlFor="dateValidite">Date Validité</label>
                                                    <input type="text" id="dateValidite" className="form-control" value={carte.dateValidite ? format(carte.dateValidite, 'dd/MM/yyyy') : ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label htmlFor="dateValidite">Date Extrême Validité</label>
                                                    <input type="text" id="dateExtremeValidite" className="form-control" value={carte.dateExtremeValidite ? format(carte.dateExtremeValidite, 'dd/MM/yyyy') : ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label htmlFor="produit">Produit</label>
                                                    <input type="text" id="produit" className="form-control" value={carte.designationArticle ?? 'Tous'} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xl-4 col-lg-6">
                                        <div className="card" style={cardStyle}>
                                            <div className="card-body">
                                                {carte.idArticle ? (
                                                    <>
                                                        <div className="col-md-12">
                                                            <label htmlFor="solde">Solde Quantité</label>
                                                            <input type="text" id="solde" className="form-control text-end" value={new Intl.NumberFormat('fr-FR', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(carte.solde ?? 0) + ' L'} readOnly />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="col-md-12">
                                                            <label htmlFor="solde">Solde Valeur</label>
                                                            <input type="text" id="solde" className="form-control text-end" value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(carte.solde ?? 0)} readOnly />
                                                        </div>
                                                    </>
                                                )}

                                                <div className="col-md-12">
                                                    <label>Limite mensuelle Essence</label>
                                                    <input type="text" className="form-control text-end" value={carte.limiteMensuelEs ? `${carte.limiteMensuelEs.toFixed(2).replace('.', ',')} L` : ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label>Limite mensuelle Gasoil</label>
                                                    <input type="text" className="form-control text-end" value={carte.limiteMensuelGo ? `${carte.limiteMensuelGo.toFixed(2).replace('.', ',')} L` : ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label>Limite prise</label>
                                                    <input type="text" className="form-control text-end" value={carte.limitePrise ? `${carte.limitePrise.toFixed(2).replace('.', ',')} L` : ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label>Statut</label>
                                                    <input type="text" className="form-control text-end" value={carte.statut ?? ''} readOnly />
                                                </div>

                                                <div className="col-md-12">
                                                    <label>Zone géographique</label>
                                                    <input type="text" className="form-control text-end" value={carte.nomBranche ?? ''} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xl-4 col-lg-6">
                                        <div className="card" style={cardStyle}>
                                            <div className="card-body">
                                                <div className="col-md-12">
                                                    <input type="checkbox" id="noLimitUpToMonthLimit" checked={carte.noLimitUpToMonthLimit ?? false} /> <label htmlFor="noLimitUpToMonthLimit">Permettre livraison sans recharge</label>
                                                </div>

                                                <div className="hr-text">#</div>

                                                <div className="col-md-12">
                                                    <label>Code PIN</label>
                                                    <div className="input-group">
                                                        <input type={showPin ? 'text' : 'password'} className="form-control text-end" value={carte.codePin ?? ''} readOnly />
                                                        <button className="btn btn-outline-secondary" type="button" onClick={() => setShowPin((prev) => !prev)} aria-label={showPin ? 'Masquer le code PIN' : 'Afficher le code PIN'}>
                                                            {showPin ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* {user?.roles?.includes('Admin') && (
                                                    <>
                                                        <div className="col-md-12">
                                                            <label>RFID Code</label>
                                                            <input type="text" className="form-control text-end" value={carte.rfidCode ?? ''} readOnly />
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label>QR Code</label>
                                                            <input type="text" className="form-control text-end" value={carte.codeQr ?? ''} readOnly />
                                                        </div>
                                                    </>
                                                )} */}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {!user?.roles?.includes('AdminViewer') && (
                                    <>
                                        <div className="hr-text">#</div>

                                        <div>
                                            <div className="flex items-center gap-2 justify-end ">
                                                <EditCarteModal carte={carte} onUpdate={handleCarteUpdated} />

                                                {(!!carte.lockoutEnd || carte.isLockedPermanently === true) && (
                                                    <button type="button" className="btn btn-1 mx-1" onClick={handleUnlockCard}>
                                                        <IconLockOpen size={16} color="currentColor" className="icon icon-tabler icons-tabler-outline icon-tabler-edit" />
                                                        Déverrouiller
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        </div>

                        <div className="tab-pane" id="tabs-recharges">
                            <RechargeCarteMasterGrid recharges={recharges} onCancel={() => {}} />
                        </div>

                        <div className="tab-pane" id="tabs-journal">
                            <JournalCarteGrid articleId={carte?.idArticle} journalCartes={journalItems} />
                        </div>

                        <div className="tab-pane" id="tabs-log">
                            <CarteGrid cartes={logs} codeClient={codeClient} auditView={true} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const CarteDetails: React.FC<CarteDetailsProps> = ({ pCarte, codeClient, onUpdate, onClose }) => {
    const [showModal, setShowModal] = useState(false);

    const handleClose = () => {
        setShowModal(false);
        onClose?.();
    };
    const handleShow = () => setShowModal(true);

    // ---

    return (
        <>
            <button
                onClick={handleShow}
                style={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#0066cc',
                    textDecoration: 'underline',
                }}
            >
                {pCarte.numero || ''}
            </button>

            <Modal show={showModal} onHide={handleClose} size="xl" scrollable backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Détails Carte #{pCarte.numero}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <CarteDetailsContent pCarte={pCarte} codeClient={codeClient} onUpdate={onUpdate} showModal={showModal} />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default CarteDetails;
